
import React, { useState, useRef, useEffect, Suspense, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useSpring, useTransform, useInView } from 'framer-motion';


// ... (existing imports)

// Sub-component for Animated Size Bar
const SizeBar = ({ height, color, label, sizeCm, delay }) => {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: "-50px" });

    // Spring animation for height
    const springHeight = useSpring(0, { stiffness: 60, damping: 15, mass: 1, restDelta: 0.001 });

    // Spring animation for counter
    const springCounter = useSpring(0, { stiffness: 60, damping: 15 });
    const countDisplay = useTransform(springCounter, (latest) => `${Math.round(latest)}cm`);

    useEffect(() => {
        if (isInView) {
            // Add a small delay for staggered effect
            const timer = setTimeout(() => {
                springHeight.set(height); // e.g. "60%" or "80%" passed as number 60/80 for mapping but needed as string for style, wait...
                // actually let's pass percentage as number 60, 80, 100
                springCounter.set(sizeCm);
            }, delay * 1000);
            return () => clearTimeout(timer);
        }
    }, [isInView, height, sizeCm, delay, springHeight, springCounter]);

    return (
        <div ref={ref} className="h-full w-full flex flex-col justify-end relative group">
            <motion.div
                className={`w-full rounded-t-xl relative overflow-hidden backdrop-blur-md border border-${color === 'cyan' ? '[#00f2ea]' : color === 'magenta' ? '[#ff0050]' : '[#ffee00]'}/30`}
                style={{
                    height: useTransform(springHeight, value => `${value}%`),
                    background: `linear-gradient(to top, 
                        ${color === 'cyan' ? 'rgba(0, 242, 234, 0.2)' : color === 'magenta' ? 'rgba(255, 0, 80, 0.2)' : 'rgba(255, 238, 0, 0.2)'}, 
                        transparent)`
                }}
            >
                {/* Neon Top Edge */}
                <div className={`absolute top-0 left-0 right-0 h-[2px] shadow-[0_0_10px_2px_${color === 'cyan' ? 'rgba(0,242,234,0.8)' : color === 'magenta' ? 'rgba(255,0,80,0.8)' : 'rgba(255,238,0,0.8)'}] bg-${color === 'cyan' ? '[#00f2ea]' : color === 'magenta' ? '[#ff0050]' : '[#ffee00]'}`} />

                {/* Floating Counter */}
                <motion.div className="absolute inset-0 flex items-center justify-center">
                    <motion.span className={`font-black text-xl md:text-2xl text-${color === 'cyan' ? '[#00f2ea]' : color === 'magenta' ? '[#ff0050]' : '[#ffee00]'}`}>
                        {countDisplay}
                    </motion.span>
                </motion.div>
            </motion.div>

            {/* Label below bar if needed, or inside? Design showed label inside S, M, L */}
            {/* The user request asked for S M L blocks to fill up. The original design had S M L inside. Let's stack them. */}
            <div className={`text-center mt-2 font-black text-2xl text-${color === 'cyan' ? '[#00f2ea]' : color === 'magenta' ? '[#ff0050]' : '[#ffee00]'} uppercase`}>
                {label}
            </div>
        </div>
    );
};


import { ShoppingBag, Star, Sparkles, Shield, ArrowRight, TrendingUp, Zap, Check, X, Loader2, ShoppingCart, Info, Play, Pause, ChevronLeft, ChevronRight, Truck, Gift } from 'lucide-react';
import { useCart } from '../context/CartContext';
import PrimeDrawer from '../components/cart/PrimeDrawer';
import GrowthTimeline from '../components/marketing/GrowthTimeline';

import { supabase } from '../supabaseClient';
import SyncFeed from '../components/ui/SyncFeed';
import { trackEvent } from '../utils/Tracking';
import { products } from '../data/products';
import PromoQuiz from '../components/gamification/PromoQuiz';

const GuideSection = React.lazy(() => import('../components/GuideSection'));
const ComparisonTable = React.lazy(() => import('../components/marketing/ComparisonTable'));
const SolutionSection = React.lazy(() => import('../components/marketing/SolutionSection'));
const RiskReversalGuarantee = React.lazy(() => import('../components/marketing/RiskReversalGuarantee'));
const ScentParticles = React.lazy(() => import('../components/ui/ScentParticles'));
const NewBrandSection = React.lazy(() => import('../components/marketing/NewBrandSection'));
const ConfiguratorHero = React.lazy(() => import('../components/marketing/ConfiguratorHero'));

const LooxReviews = React.lazy(() => import('../components/marketing/LooxReviews'));
const ScentFusionSection = React.lazy(() => import('../components/marketing/ScentFusionSection'));


// Global registry to track all video elements and pause them
let activeVideoRef = null;

// --- TikTok Live Interactions Component ---
const TikTokLiveInteractions = () => {
    return (
        <div className="absolute bottom-4 right-4 z-20 flex flex-col items-center gap-1 opacity-80 pointer-events-none">
            {[...Array(6)].map((_, i) => (
                <motion.div
                    key={i}
                    initial={{ y: 0, opacity: 0, scale: 0.5, x: 0 }}
                    animate={{
                        y: [-20, -150],
                        opacity: [0, 1, 1, 0],
                        scale: [0.5, 1.2, 1, 0.8],
                        x: [0, (i % 2 === 0 ? 30 : -30), 0]
                    }}
                    transition={{
                        duration: 3,
                        repeat: Infinity,
                        delay: i * 0.5,
                        ease: "easeOut"
                    }}
                    className="absolute bottom-0"
                >
                    {i % 3 === 0 ? (
                        <div className="text-[#ff0050] drop-shadow-[0_0_8px_rgba(255,0,80,0.8)]">❤️</div>
                    ) : i % 3 === 1 ? (
                        <div className="text-[#00f2ea] drop-shadow-[0_0_8px_rgba(0,242,234,0.8)]">❤️</div>
                    ) : (
                        <div className="bg-white/20 p-1 rounded-full backdrop-blur-sm">
                            <svg className="w-3 h-3 text-white fill-current" viewBox="0 0 24 24">
                                <path d="M24 10.535v3c0 .356-.162.684-.447.9l-11.833 9c-.276.21-.635.312-1.02.312-.416 0-.812-.112-1.127-.323-.33-.22-.52-.52-.882v-3.771l-1.053-.024c-2.88-.063-4.821-.497-6.098-1.353-2.028-1.36-2.583-3.778-1.558-6.173.23-.538.528-1.051.89-1.536.082-.11.23-.153.36-.105l1.638.6c.13.048.204.183.175.319a5.183 5.183 0 0 0-.156 1.432c0 1.21.656 2.373 1.846 3.2 1.144.793 2.723 1.168 4.793 1.103l1.162-.036v-6.203c0-.362.19-.662.52-.882.315-.211.711-.323 1.127-.323.385 0 .744.102 1.02.312l11.833 9c.285.216.447.544.447.9zm-2.181 0l-10-7.611v5.183c0 .276-.224.5-.5.5-2.923 0-5.111-.354-6.877-1.103a8.97 8.97 0 0 1-1.351-.715c-.273.666-.376 1.348-.288 1.996.113.834.469 1.411 1.058 1.716C5.071 11.14 6.784 11.517 9.047 11.567l1.634.036a.5.5 0 0 1 .488.498v4.904l10.65-8.035v-8.435z" />
                            </svg>
                        </div>
                    )}
                </motion.div>
            ))}
            <div className="bg-black/40 px-2 py-0.5 rounded-full text-[10px] font-bold text-white mb-2">
                12.4k partages
            </div>
        </div>
    );
};

// --- COMPOSANT VIDEO CARD (CLICK TO PLAY) ---
const VideoCard = ({ video, user, views, image }) => {
    const videoRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);

    // Use the provided image directly
    const thumbnail = image;

    const togglePlay = () => {
        if (isPlaying) {
            // Stop playing
            setIsPlaying(false);
            activeVideoRef = null;
        } else {
            // Start playing
            // Pause any other playing video
            if (activeVideoRef && activeVideoRef !== videoRef.current) {
                // If the other video is mounted (it should be if active), pause it
                try {
                    activeVideoRef.pause();
                    activeVideoRef.dispatchEvent(new Event('externalPause'));
                } catch (e) {
                    console.log("Error pausing other video", e);
                }
            }
            setIsPlaying(true);
            // activeVideoRef will be set via ref callback when video mounts
        }
    };

    return (
        <div
            className="relative bg-black aspect-[9/16] rounded-2xl overflow-hidden border-4 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] hover:translate-y-1 hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transition-all flex flex-col justify-end group cursor-pointer"
            onClick={togglePlay}
        >
            {/* Thumbnail/Poster when not playing */}
            {!isPlaying && thumbnail && (
                <img
                    src={thumbnail}
                    alt={user}
                    className="absolute inset-0 w-full h-full object-cover"
                />
            )}

            {/* Video - Only rendered when playing (LAZY LOADING) */}
            {isPlaying && video && (
                <video
                    ref={(el) => {
                        videoRef.current = el;
                        if (el) {
                            activeVideoRef = el;
                            el.play().catch(e => console.log("Play failed", e));

                            // Listen for external pause
                            const handleExternalPause = () => setIsPlaying(false);
                            el.addEventListener('externalPause', handleExternalPause);
                            // Cleanup listener is tricky with ref callback, but acceptable here as component unmounts video on !isPlaying
                        }
                    }}
                    src={video}
                    playsInline
                    className="absolute inset-0 w-full h-full object-cover"
                    onEnded={() => {
                        setIsPlaying(false);
                        activeVideoRef = null;
                    }}
                />
            )}

            {/* Play Button Overlay */}
            {!isPlaying && video && (
                <div className="absolute inset-0 flex items-center justify-center z-20 bg-black/20 group-hover:bg-black/10 transition-colors">
                    <div className="w-16 h-16 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center border-2 border-white/50 shadow-lg transform group-hover:scale-110 transition-transform">
                        <Play size={32} fill="white" className="text-white ml-1" />
                    </div>
                </div>
            )}

            <div className={`relative z - 10 p - 4 bg - gradient - to - t from - black / 90 to - transparent pt - 12 transition - opacity duration - 300 ${isPlaying ? 'opacity-0 hover:opacity-100' : 'opacity-100'} `}>
                <div className="font-black text-white text-sm truncate">{user}</div>
                <div className="text-xs font-bold text-[#ff0050] flex items-center gap-1"><TrendingUp size={12} /> {views} VUES</div>
            </div>
        </div>
    );
};


// --- COMPOSANT CREEPY BUTTON (VERSION STABLE) ---
// --- COMPOSANT CREEPY BUTTON (VERSION STABLE) ---
const CreepyButton = ({ onClick, text, className = "", icon: Icon, isSuccess = false, borderColor = "border-black" }) => (
    <div className="relative group inline-block">
        {/* YEUX (Cachés derrière, visibles au survol quand le bouton bouge) */}
        <div className="absolute inset-0 bg-black rounded-full flex items-end justify-end pb-3 pr-6 gap-2 z-0">
            <div className="w-3 h-3 bg-white rounded-full animate-blink flex items-center justify-center">
                <div className="w-1 h-1 bg-black rounded-full"></div>
            </div>
            <div className="w-3 h-3 bg-white rounded-full animate-blink flex items-center justify-center" style={{ animationDelay: '0.2s' }}>
                <div className="w-1 h-1 bg-black rounded-full"></div>
            </div>
        </div>

        {/* BOUTON PRINCIPAL (Au-dessus) */}
        <button
            onClick={onClick}
            className={`relative z-10 w-full h-full font-black rounded-full border-2 flex items-center justify-center gap-2 transition-all duration-300 ease-out active:scale-95 md:group-hover:-translate-y-2 md:group-hover:-translate-x-1 shadow-[8px_8px_0px_0px_rgba(255,255,255,0.2)] md:group-hover:shadow-[12px_12px_0px_0px_rgba(255,255,255,0.2)] ${isSuccess ? 'bg-[#00f2ea] text-black border-[#00f2ea]' : `bg-black text-white ${borderColor}`} ${className}`}
        >
            <span className="glitch-voltage" data-text={text}>{text}</span>
            {Icon && <Icon size={20} />}
        </button>
    </div>
);



// --- COMPOSANT DELIVERY INFO MODAL ---
const DeliveryInfoModal = ({ isOpen, onClose }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-md overflow-hidden relative p-8 text-center">
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-black">
                    <X size={24} />
                </button>
                <div className="w-20 h-20 bg-[#00A8E1] rounded-full flex items-center justify-center mx-auto mb-6">
                    <div className="text-white font-black italic text-2xl flex flex-col items-center leading-none">
                        <span>Buy with</span>
                        <span className="text-3xl">prime</span>
                    </div>
                </div>
                <h3 className="text-xl font-bold mb-4">Get fast, free delivery on your purchase from this store</h3>
                <p className="text-gray-600 mb-8 text-sm">
                    Prime members now get fast, free delivery and easy returns on eligible items directly from stores across the web.
                </p>
                <button onClick={onClose} className="w-full py-3 bg-[#00A8E1] hover:bg-[#008dbd] text-white font-bold rounded-lg transition-colors">
                    Learn more
                </button>
            </div>
        </div>
    );
};



const HomePage = () => {
    const [focusedBag, setFocusedBag] = useState(null);
    const [selectedStyle, setSelectedStyle] = useState('NEON');
    const [selectedSize, setSelectedSize] = useState('S');
    const [selectedPack, setSelectedPack] = useState(30);
    const [selectedColor, setSelectedColor] = useState('cyan');
    const [activeCard, setActiveCard] = useState(null); // State pour la carte active unique
    const [showPrimeDrawer, setShowPrimeDrawer] = useState(false);
    const [showDeliveryInfo, setShowDeliveryInfo] = useState(false);
    const [activeTestimonial, setActiveTestimonial] = useState(0);
    const carouselRef = useRef(null);
    const buyboxRef = useRef(null);
    const [showPromoQuiz, setShowPromoQuiz] = useState(false);
    const [promoConfig, setPromoConfig] = useState(null);
    const [isSnapLocked, setIsSnapLocked] = useState(false);
    const snapCooldownRef = useRef(false);

    // CONFIGURATOR_SECTION_HIDDEN - Set to true to show the configurator section
    // Demander "montre moi CONFIGURATOR_SECTION_HIDDEN" pour la réactiver
    const SHOW_CONFIGURATOR = false;

    // Force scroll to top on mount
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // 🧲 MAGNETIC SNAP SYSTEM
    const scrollDirectionRef = useRef('down');
    const lastScrollYRef = useRef(0);
    const hasSnappedDuringView = useRef(false); // Prevents double-snapping

    // Track scroll direction globally and accurately
    useEffect(() => {
        const handleScroll = () => {
            const current = window.scrollY;
            if (Math.abs(current - lastScrollYRef.current) > 5) { // Threshold to ignore micro-scrolls
                scrollDirectionRef.current = current > lastScrollYRef.current ? 'down' : 'up';
                lastScrollYRef.current = current;
            }
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // 🔒 SNAP LOCK LOGIC
    useEffect(() => {
        const buybox = buyboxRef.current;
        if (!buybox) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    // RESET SNAP STATE when user leaves the section (scrolled away)
                    if (entry.intersectionRatio < 0.1) {
                        hasSnappedDuringView.current = false;
                    }

                    // Only snap if:
                    // 1. Mostly visible (>= 65%)
                    // 2. Scrolling DOWN
                    // 3. Not locked
                    // 4. Not in cooldown
                    // 5. HAS NOT ALREADY SNAPPED in this session (The fix)
                    if (
                        entry.intersectionRatio >= 0.65 &&
                        scrollDirectionRef.current === 'down' &&
                        !isSnapLocked &&
                        !snapCooldownRef.current &&
                        !hasSnappedDuringView.current
                    ) {
                        setIsSnapLocked(true);
                        snapCooldownRef.current = true;
                        hasSnappedDuringView.current = true; // Mark as done for this view session

                        // Lock body
                        document.body.style.overflow = 'hidden';

                        // Notify Navbar
                        window.dispatchEvent(new CustomEvent('buybox-snap', { detail: { locked: true } }));
                        buybox.setAttribute('data-snap-locked', 'true');

                        // Reset cooldown logic (optional now, but kept for safety)
                        setTimeout(() => {
                            snapCooldownRef.current = false;
                        }, 1200);
                    }
                });
            },
            { threshold: [0.1, 0.65, 0.7, 0.8] } // Added 0.1 to detect exit
        );

        observer.observe(buybox);

        return () => {
            observer.disconnect();
            document.body.style.overflow = ''; // Ensure we unlock on unmount
        };
    }, [isSnapLocked]);

    // 🔓 SNAP RELEASE LOGIC (Direction Aware)
    useEffect(() => {
        if (!isSnapLocked) return;

        let releaseTimeout = null;

        const handleReleaseAttempt = (e) => {
            // Debounce release slightly
            if (releaseTimeout) return;

            // Determine intent (Up or Down)
            // Reduced threshold for easier release (Was 10)
            const isIntentionalRelease = Math.abs(e.deltaY) > 5 || e.type === 'touchmove';

            if (isIntentionalRelease) {
                // Execute release faster
                releaseTimeout = setTimeout(() => {
                    setIsSnapLocked(false);
                    document.body.style.overflow = '';
                    window.dispatchEvent(new CustomEvent('buybox-snap', { detail: { locked: false } }));

                    const buybox = buyboxRef.current;
                    // Natural Release: Push user slightly in their intended direction
                    if (buybox) {
                        const direction = e.deltaY > 0 ? 1 : -1;

                        if (e.type === 'touchmove') {
                            // For touch, we just unlock and let momentum take over
                        } else {
                            // For wheel, give a nudge INTELLIGENTLY
                            // Remove 'smooth' to prevent conflict with layout shift (fixed -> relative)
                            // Small nudge to restart momentum
                            window.scrollBy({ top: direction * 50, behavior: 'auto' });
                        }
                    }
                    releaseTimeout = null;

                    // Extend cooldown slightly after release to prevent bounce-back
                    snapCooldownRef.current = true;
                    setTimeout(() => { snapCooldownRef.current = false; }, 800);

                }, 20); // Faster reaction (Was 50)
            }
        };

        window.addEventListener('wheel', handleReleaseAttempt, { passive: true });
        window.addEventListener('touchmove', handleReleaseAttempt, { passive: true });

        return () => {
            window.removeEventListener('wheel', handleReleaseAttempt);
            window.removeEventListener('touchmove', handleReleaseAttempt);
            if (releaseTimeout) clearTimeout(releaseTimeout);
        };
    }, [isSnapLocked]);

    // Testimonials data
    // Testimonials data - UPDATED
    const testimonials = [
        {
            image: '/assets/avis-client-marie-sac-vinted-5-etoiles.webp',
            name: 'Marie D.',
            title: 'TOP VENDEUSE VINTED',
            quote: '« Depuis que j\'utilise Unbox Creator, mes notes sont passées de 4.7 à 4.9. Mes clients adorent l\'expérience d\'ouverture. »'
        },
        {
            image: '/assets/avis-client-thomas-pochette-colis-premium.webp',
            name: 'Thomas L.',
            title: 'STORE MANAGER',
            quote: '« Le parfum signature a transformé ma marque. C\'est devenu mon avantage concurrentiel numéro un sur le marché. »'
        },
        {
            image: '/assets/avis-client-sophie-emballage-parfume-satisfait.webp',
            name: 'Sophie M.',
            title: 'FASHION INFLUENCER',
            quote: '« Chaque unboxing devient viral. Mes followers adorent le design Neon. J\'ai triplé mes ventes grâce à l\'effet Instagram. »',
            className: 'scale-[1.15]'
        },
        {
            image: '/assets/avis-client-julie-expedition-vinted-rapide.webp',
            name: 'Julie R.',
            title: 'CRÉATRICE ETSY',
            quote: '« Mes créations méritent un emballage à la hauteur. Avec Unbox Creator, chaque colis est une expérience de luxe. »'
        }
    ];

    // Safe access to cart context
    const cartContext = useCart();
    const addItem = cartContext?.addItem || (() => console.warn('Cart Provider missing'));


    const unicolorColors = useMemo(() => {
        return products["enveloppe-ecommerce-compatible-shopify"].colors;
    }, []);

    const productStyles = useMemo(() => ({
        NEON: {
            name: 'NEON',
            image: products["sac-viral-compatible-tiktok-neon"].images[0],
            badge: products["sac-viral-compatible-tiktok-neon"].tag
        },
        ZIPPER: {
            name: 'ZIPPER',
            image: products["pochette-premium-compatible-vinted-zipper"].images[0],
            badge: products["pochette-premium-compatible-vinted-zipper"].tag
        },
        UNICOLOR: {
            name: 'UNICOLOR',
            image: `/ assets / unicolor - ${selectedColor}.webp`,
            badge: products["enveloppe-ecommerce-compatible-shopify"].tag
        }
    }), [selectedColor]);

    const currentProduct = productStyles[selectedStyle];

    const [isAdded, setIsAdded] = useState(false);

    const pricingMap = useMemo(() => ({
        NEON: products["sac-viral-compatible-tiktok-neon"].pricing,
        ZIPPER: products["pochette-premium-compatible-vinted-zipper"].pricing,
        UNICOLOR: products["enveloppe-ecommerce-compatible-shopify"].pricing
    }), []);

    const calculatePrice = () => {
        try {
            return pricingMap[selectedStyle][selectedPack][selectedSize];
        } catch (e) {
            console.error("Pricing error", e);
            return 0;
        }
    };

    const handleAddToCart = async () => {
        const item = {
            id: `${selectedStyle} -${selectedSize} -${selectedPack} -${selectedColor} `,
            name: currentProduct.name,
            style: selectedStyle,
            size: selectedSize,
            pack: selectedPack,
            color: selectedColor,
            price: calculatePrice(),
            image: currentProduct.image,
            qty: 1
        };

        addItem(item);

        // --- TRACKING EVENT: ADD TO CART ---
        trackEvent('AddToCart', {
            content_name: item.name,
            content_category: item.style,
            value: item.price,
            currency: 'EUR'
        });

        if (import.meta.env.MODE !== 'development') {
            try {
                // Retrieve Session & Geo from Storage (Same as App.jsx)
                const sessionId = localStorage.getItem('visitor_session_id');
                const geo = JSON.parse(sessionStorage.getItem('visitor_geo') || '{}');

                if (sessionId) {
                    await supabase.from('visits').insert([{
                        page: '/event/add-to-cart', // Special flag for Live View
                        ref: document.referrer,
                        session_id: sessionId,
                        city: geo?.city || 'Unknown',
                        country: geo?.country_name || 'Unknown',
                        lat: geo?.latitude || 0,
                        lng: geo?.longitude || 0
                    }]);
                }
            } catch (err) {
                // Silent fail to not block UI
                console.error("Tracking Error", err);
            }
        }
    };

    return (
        <div className="min-h-screen bg-white text-black">

            <Helmet>
                {/* TITRE SEO - MOTS-CLÉS NUCLEAR STRATEGY */}
                <title>Sac Expédition Parfumé | Pochette Colis & Emballage - Unbox Creator</title>
                <meta name="title" content="Sac Expédition Parfumé | Pochette Colis Vinted | Emballage Colis" />

                <meta name="description" content="Sac expédition parfumé n°1 en France. Pochette colis ultra-résistante 60µ pour Vinted, e-commerce. Emballage colis premium parfumé. Livraison 24h. Pack dès 12,99€." />

                <link rel="canonical" href="https://unboxcreator.com/" />

                {/* OPEN GRAPH - MOTS RECHERCHÉS */}
                <meta property="og:type" content="product" />
                <meta property="og:url" content="https://unboxcreator.com/" />
                <meta property="og:title" content="Sac Expédition Parfumé | Pochette Colis & Emballage Premium" />
                <meta property="og:description" content="Sac expédition et pochette colis parfumés. L'emballage n°1 pour vendeurs Vinted et e-commerce. Ultra-résistant 60 microns." />
                <meta property="og:image" content="https://unboxcreator.com/assets/sac-vinted-hero-emballage-parfume-3d.webp" />
                <meta property="og:locale" content="fr_FR" />
                <meta property="og:site_name" content="Unbox Creator" />

                {/* SCHEMA.ORG PRODUCT - GAMME NEON */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "Product",
                        "name": "Sac Viral Compatible TikTok - Gamme NEON",
                        "image": "https://unboxcreator.com/assets/sac-vinted-hero-emballage-parfume-3d.webp",
                        "description": "Le sac qui rend vos colis viraux sur TikTok. Couleur flashy visible à 100m, parfum fruité intégré, indéchirable 60 microns. Booste les posts TikTok/Insta.",
                        "brand": { "@type": "Brand", "name": "Unbox Creator" },
                        "sku": "UC-NEON",
                        "material": "Polyéthylène LDPE 60 microns",
                        "offers": [
                            { "@type": "Offer", "name": "Pack 30 - Taille S", "sku": "UC-NEON-30-S", "price": "12.99", "priceCurrency": "EUR", "availability": "https://schema.org/InStock", "url": "https://unboxcreator.com/#buybox", "priceValidUntil": "2027-12-31", "itemCondition": "https://schema.org/NewCondition", "productID": "UC-NEON-30-S" },
                            { "@type": "Offer", "name": "Pack 30 - Taille M", "sku": "UC-NEON-30-M", "price": "14.99", "priceCurrency": "EUR", "availability": "https://schema.org/InStock", "url": "https://unboxcreator.com/#buybox", "priceValidUntil": "2027-12-31", "itemCondition": "https://schema.org/NewCondition", "productID": "UC-NEON-30-M" },
                            { "@type": "Offer", "name": "Pack 30 - Taille L", "sku": "UC-NEON-30-L", "price": "16.99", "priceCurrency": "EUR", "availability": "https://schema.org/InStock", "url": "https://unboxcreator.com/#buybox", "priceValidUntil": "2027-12-31", "itemCondition": "https://schema.org/NewCondition", "productID": "UC-NEON-30-L" },
                            { "@type": "Offer", "name": "Pack 100 - Taille S", "sku": "UC-NEON-100-S", "price": "35.99", "priceCurrency": "EUR", "availability": "https://schema.org/InStock", "url": "https://unboxcreator.com/#buybox", "priceValidUntil": "2027-12-31", "itemCondition": "https://schema.org/NewCondition", "productID": "UC-NEON-100-S" },
                            { "@type": "Offer", "name": "Pack 100 - Taille M", "sku": "UC-NEON-100-M", "price": "39.99", "priceCurrency": "EUR", "availability": "https://schema.org/InStock", "url": "https://unboxcreator.com/#buybox", "priceValidUntil": "2027-12-31", "itemCondition": "https://schema.org/NewCondition", "productID": "UC-NEON-100-M" },
                            { "@type": "Offer", "name": "Pack 100 - Taille L", "sku": "UC-NEON-100-L", "price": "43.99", "priceCurrency": "EUR", "availability": "https://schema.org/InStock", "url": "https://unboxcreator.com/#buybox", "priceValidUntil": "2027-12-31", "itemCondition": "https://schema.org/NewCondition", "productID": "UC-NEON-100-L" },
                            { "@type": "Offer", "name": "Pack 300 - Taille S", "sku": "UC-NEON-300-S", "price": "89.99", "priceCurrency": "EUR", "availability": "https://schema.org/InStock", "url": "https://unboxcreator.com/#buybox", "priceValidUntil": "2027-12-31", "itemCondition": "https://schema.org/NewCondition", "productID": "UC-NEON-300-S" },
                            { "@type": "Offer", "name": "Pack 300 - Taille M", "sku": "UC-NEON-300-M", "price": "99.99", "priceCurrency": "EUR", "availability": "https://schema.org/InStock", "url": "https://unboxcreator.com/#buybox", "priceValidUntil": "2027-12-31", "itemCondition": "https://schema.org/NewCondition", "productID": "UC-NEON-300-M" },
                            { "@type": "Offer", "name": "Pack 300 - Taille L", "sku": "UC-NEON-300-L", "price": "109.99", "priceCurrency": "EUR", "availability": "https://schema.org/InStock", "url": "https://unboxcreator.com/#buybox", "priceValidUntil": "2027-12-31", "itemCondition": "https://schema.org/NewCondition", "productID": "UC-NEON-300-L" }
                        ],
                        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.9", "reviewCount": "124", "bestRating": "5" }
                    })}
                </script>

                {/* SCHEMA.ORG PRODUCT - GAMME ZIPPER */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "Product",
                        "name": "Pochette Premium Compatible Vinted - Gamme ZIPPER",
                        "image": "https://unboxcreator.com/assets/sac-vinted-zipper-pochette-expedition-premium.webp",
                        "description": "L'emballage boutique de luxe, réutilisable. Finition givrée, fermeture adhésive sécurisée. Idéal pour les vendeurs Vinted Pro.",
                        "brand": { "@type": "Brand", "name": "Unbox Creator" },
                        "sku": "UC-ZIPPER",
                        "material": "Polyéthylène LDPE 60 microns givré",
                        "offers": [
                            { "@type": "Offer", "name": "Pack 30 - Taille S", "sku": "UC-ZIPPER-30-S", "price": "12.99", "priceCurrency": "EUR", "availability": "https://schema.org/InStock", "url": "https://unboxcreator.com/#buybox", "priceValidUntil": "2027-12-31", "itemCondition": "https://schema.org/NewCondition", "productID": "UC-ZIPPER-30-S" },
                            { "@type": "Offer", "name": "Pack 30 - Taille M", "sku": "UC-ZIPPER-30-M", "price": "14.99", "priceCurrency": "EUR", "availability": "https://schema.org/InStock", "url": "https://unboxcreator.com/#buybox", "priceValidUntil": "2027-12-31", "itemCondition": "https://schema.org/NewCondition", "productID": "UC-ZIPPER-30-M" },
                            { "@type": "Offer", "name": "Pack 30 - Taille L", "sku": "UC-ZIPPER-30-L", "price": "16.99", "priceCurrency": "EUR", "availability": "https://schema.org/InStock", "url": "https://unboxcreator.com/#buybox", "priceValidUntil": "2027-12-31", "itemCondition": "https://schema.org/NewCondition", "productID": "UC-ZIPPER-30-L" },
                            { "@type": "Offer", "name": "Pack 100 - Taille S", "sku": "UC-ZIPPER-100-S", "price": "35.99", "priceCurrency": "EUR", "availability": "https://schema.org/InStock", "url": "https://unboxcreator.com/#buybox", "priceValidUntil": "2027-12-31", "itemCondition": "https://schema.org/NewCondition", "productID": "UC-ZIPPER-100-S" },
                            { "@type": "Offer", "name": "Pack 100 - Taille M", "sku": "UC-ZIPPER-100-M", "price": "39.99", "priceCurrency": "EUR", "availability": "https://schema.org/InStock", "url": "https://unboxcreator.com/#buybox", "priceValidUntil": "2027-12-31", "itemCondition": "https://schema.org/NewCondition", "productID": "UC-ZIPPER-100-M" },
                            { "@type": "Offer", "name": "Pack 100 - Taille L", "sku": "UC-ZIPPER-100-L", "price": "43.99", "priceCurrency": "EUR", "availability": "https://schema.org/InStock", "url": "https://unboxcreator.com/#buybox", "priceValidUntil": "2027-12-31", "itemCondition": "https://schema.org/NewCondition", "productID": "UC-ZIPPER-100-L" },
                            { "@type": "Offer", "name": "Pack 300 - Taille S", "sku": "UC-ZIPPER-300-S", "price": "89.99", "priceCurrency": "EUR", "availability": "https://schema.org/InStock", "url": "https://unboxcreator.com/#buybox", "priceValidUntil": "2027-12-31", "itemCondition": "https://schema.org/NewCondition", "productID": "UC-ZIPPER-300-S" },
                            { "@type": "Offer", "name": "Pack 300 - Taille M", "sku": "UC-ZIPPER-300-M", "price": "99.99", "priceCurrency": "EUR", "availability": "https://schema.org/InStock", "url": "https://unboxcreator.com/#buybox", "priceValidUntil": "2027-12-31", "itemCondition": "https://schema.org/NewCondition", "productID": "UC-ZIPPER-300-M" },
                            { "@type": "Offer", "name": "Pack 300 - Taille L", "sku": "UC-ZIPPER-300-L", "price": "109.99", "priceCurrency": "EUR", "availability": "https://schema.org/InStock", "url": "https://unboxcreator.com/#buybox", "priceValidUntil": "2027-12-31", "itemCondition": "https://schema.org/NewCondition", "productID": "UC-ZIPPER-300-L" }
                        ],
                        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "5.0", "reviewCount": "89", "bestRating": "5" }
                    })}
                </script>

                {/* SCHEMA.ORG PRODUCT - GAMME UNICOLOR */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "Product",
                        "name": "Enveloppe E-commerce - Gamme UNICOLOR",
                        "image": "https://unboxcreator.com/assets/unicolor-cyan.webp",
                        "description": "Votre identité de marque à petit prix. 10 couleurs disponibles. Qualité professionnelle 60 microns. Compatible Shopify, Etsy et Vinted.",
                        "brand": { "@type": "Brand", "name": "Unbox Creator" },
                        "sku": "UC-UNICOLOR",
                        "material": "Polyéthylène LDPE 60 microns",
                        "color": "10 couleurs disponibles",
                        "offers": [
                            { "@type": "Offer", "name": "Pack 30 - Taille S", "sku": "UC-UNICOLOR-30-S", "price": "9.99", "priceCurrency": "EUR", "availability": "https://schema.org/InStock", "url": "https://unboxcreator.com/#buybox", "priceValidUntil": "2027-12-31", "itemCondition": "https://schema.org/NewCondition", "productID": "UC-UNICOLOR-30-S" },
                            { "@type": "Offer", "name": "Pack 30 - Taille M", "sku": "UC-UNICOLOR-30-M", "price": "11.99", "priceCurrency": "EUR", "availability": "https://schema.org/InStock", "url": "https://unboxcreator.com/#buybox", "priceValidUntil": "2027-12-31", "itemCondition": "https://schema.org/NewCondition", "productID": "UC-UNICOLOR-30-M" },
                            { "@type": "Offer", "name": "Pack 30 - Taille L", "sku": "UC-UNICOLOR-30-L", "price": "14.99", "priceCurrency": "EUR", "availability": "https://schema.org/InStock", "url": "https://unboxcreator.com/#buybox", "priceValidUntil": "2027-12-31", "itemCondition": "https://schema.org/NewCondition", "productID": "UC-UNICOLOR-30-L" },
                            { "@type": "Offer", "name": "Pack 100 - Taille S", "sku": "UC-UNICOLOR-100-S", "price": "24.99", "priceCurrency": "EUR", "availability": "https://schema.org/InStock", "url": "https://unboxcreator.com/#buybox", "priceValidUntil": "2027-12-31", "itemCondition": "https://schema.org/NewCondition", "productID": "UC-UNICOLOR-100-S" },
                            { "@type": "Offer", "name": "Pack 100 - Taille M", "sku": "UC-UNICOLOR-100-M", "price": "29.99", "priceCurrency": "EUR", "availability": "https://schema.org/InStock", "url": "https://unboxcreator.com/#buybox", "priceValidUntil": "2027-12-31", "itemCondition": "https://schema.org/NewCondition", "productID": "UC-UNICOLOR-100-M" },
                            { "@type": "Offer", "name": "Pack 100 - Taille L", "sku": "UC-UNICOLOR-100-L", "price": "39.99", "priceCurrency": "EUR", "availability": "https://schema.org/InStock", "url": "https://unboxcreator.com/#buybox", "priceValidUntil": "2027-12-31", "itemCondition": "https://schema.org/NewCondition", "productID": "UC-UNICOLOR-100-L" },
                            { "@type": "Offer", "name": "Pack 300 - Taille S", "sku": "UC-UNICOLOR-300-S", "price": "69.99", "priceCurrency": "EUR", "availability": "https://schema.org/InStock", "url": "https://unboxcreator.com/#buybox", "priceValidUntil": "2027-12-31", "itemCondition": "https://schema.org/NewCondition", "productID": "UC-UNICOLOR-300-S" },
                            { "@type": "Offer", "name": "Pack 300 - Taille M", "sku": "UC-UNICOLOR-300-M", "price": "79.99", "priceCurrency": "EUR", "availability": "https://schema.org/InStock", "url": "https://unboxcreator.com/#buybox", "priceValidUntil": "2027-12-31", "itemCondition": "https://schema.org/NewCondition", "productID": "UC-UNICOLOR-300-M" },
                            { "@type": "Offer", "name": "Pack 300 - Taille L", "sku": "UC-UNICOLOR-300-L", "price": "99.99", "priceCurrency": "EUR", "availability": "https://schema.org/InStock", "url": "https://unboxcreator.com/#buybox", "priceValidUntil": "2027-12-31", "itemCondition": "https://schema.org/NewCondition", "productID": "UC-UNICOLOR-300-L" }
                        ],
                        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "215", "bestRating": "5" }
                    })}
                </script>

                {/* SCHEMA.ORG FAQ - Optimized for Generative AI (GEO) */}
                {/* SCHEMA.ORG FAQ - NUCLEAR SEO (10 QUESTIONS) */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "FAQPage",
                        "mainEntity": [
                            {
                                "@type": "Question",
                                "name": "Quel est le meilleur emballage pour un colis Vinted ?",
                                "acceptedAnswer": { "@type": "Answer", "text": "Le meilleur emballage pour Vinted est la pochette plastique d'expédition indéchirable. Plus légère que le carton (économie de frais de port), imperméable et opaque. Nos sacs expédition Unbox Creator ajoutent une touche unique avec leur parfum signature." }
                            },
                            {
                                "@type": "Question",
                                "name": "Où acheter des pochettes colis pas cher ?",
                                "acceptedAnswer": { "@type": "Answer", "text": "Sur Unbox Creator, nous proposons des pochettes colis premium dès 0,12€/unité. C'est le meilleur rapport qualité/prix du marché pour des sacs 60 microns ultra-résistants, bien moins cher qu'en bureau de poste." }
                            },
                            {
                                "@type": "Question",
                                "name": "Comment emballer un colis sans carton ?",
                                "acceptedAnswer": { "@type": "Answer", "text": "Utilisez une enveloppe plastique d'expédition (aussi appelée poly mailer). Glissez votre article dedans (vêtements, boîtes à chaussures, livres), retirez la bande adhésive et fermez. C'est rapide, sécurisé et accepté par Mondial Relay, Chronopost et La Poste." }
                            },
                            {
                                "@type": "Question",
                                "name": "Vos sacs d'expédition sont-ils acceptés par La Poste et Mondial Relay ?",
                                "acceptedAnswer": { "@type": "Answer", "text": "Oui, nos sacs d'expédition sont 100% homologués par tous les transporteurs : La Poste, Colissimo, Mondial Relay, Relais Colis, UPS et DHL. Ils respectent les normes d'étiquetage et de résistance." }
                            },
                            {
                                "@type": "Question",
                                "name": "Quelle taille de sac choisir pour un manteau ?",
                                "acceptedAnswer": { "@type": "Answer", "text": "Pour un manteau, une parka ou des bottes, choisissez notre taille L (35x45cm). Pour un jean ou un pull, la taille M (25x35cm) suffit. Pour un t-shirt, prenez la taille S (17x25cm)." }
                            },
                            {
                                "@type": "Question",
                                "name": "C'est quoi la technologie Scent-Fusion ?",
                                "acceptedAnswer": { "@type": "Answer", "text": "C'est notre innovation exclusive. Nous injectons du parfum directement dans la matière plastique du sac lors de sa fabrication. Le parfum ne tache pas les vêtements et offre une expérience 'unboxing' olfactive inoubliable à vos clients." }
                            },
                            {
                                "@type": "Question",
                                "name": "Ces pochettes sont-elles écologiques ?",
                                "acceptedAnswer": { "@type": "Answer", "text": "Nos pochettes sont fabriquées en LDPE recyclé et sont 100% recyclables. Moins lourdes que les cartons, elles réduisent aussi l'empreinte carbone liée au transport." }
                            },
                            {
                                "@type": "Question",
                                "name": "Peut-on utiliser ces sacs pour des retours e-commerce ?",
                                "acceptedAnswer": { "@type": "Answer", "text": "Oui, la solidité de nos sacs permet souvent une réutilisation pour un retour si l'ouverture a été faite proprement. C'est un emballage polyvalent pour tout e-commerçant." }
                            },
                            {
                                "@type": "Question",
                                "name": "Vendez-vous aux particuliers ou seulement aux pros ?",
                                "acceptedAnswer": { "@type": "Answer", "text": "Nous vendons à tout le monde ! Que vous soyez un vendeur Vinted occasionnel (particulier) ou une boutique e-commerce expédiant 500 colis par jour (pro), vous pouvez commander nos packs." }
                            },
                            {
                                "@type": "Question",
                                "name": "Quel est le délai de livraison ?",
                                "acceptedAnswer": { "@type": "Answer", "text": "Nous expédions depuis notre entrepôt en France. La livraison s'effectue généralement en 24h à 48h ouvrées partout en France métropolitaine." }
                            }
                        ]
                    })}
                </script>

                {/* SCHEMA.ORG BREADCRUMB - For site hierarchy */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "BreadcrumbList",
                        "itemListElement": [{
                            "@type": "ListItem",
                            "position": 1,
                            "name": "Accueil",
                            "item": "https://unboxcreator.com"
                        }]
                    })}
                </script>
                {/* SCHEMA.ORG ORGANIZATION (SUSPENDU - EN ATTENTE ACTIVATION COMPTES SOCIAL) */}
                {/* 
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "Organization",
                        "name": "Unbox Creator",
                        "url": "https://unboxcreator.com",
                        "logo": "https://unboxcreator.com/assets/logo.webp",
                        "sameAs": [
                            "https://www.instagram.com/unboxcreator/",
                            "https://www.tiktok.com/@unboxcreator",
                            "https://www.youtube.com/@unboxcreator"
                        ]
                    })}
                </script>
                */}
            </Helmet>

            {/* Overlay GLOBAL Fixe pour être sûr de couvrir tout sauf le sac focus */}
            <AnimatePresence>
                {focusedBag && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-white/90 backdrop-blur-md z-40 cursor-pointer"
                        onClick={() => setFocusedBag(null)}
                    />
                )}
            </AnimatePresence>

            {/* HERO SECTION : CINÉMATIQUE (RESTAURÉ) */}
            <section data-snap-target className="relative min-h-screen flex items-center pt-24 pb-12 md:pt-32 md:pb-24 px-6 bg-white overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:20px_20px] opacity-20"></div>

                <div className="max-w-7xl mx-auto w-full flex flex-col-reverse lg:grid lg:grid-cols-2 gap-6 md:gap-12 items-center relative z-10">

                    {/* LEFT: VALUE PROPOSITION */}
                    <div className="text-left space-y-4">

                        {/* 1. BADGE 'AUTHORITY' */}
                        <div className="inline-flex items-center gap-3 bg-white border-2 border-black rounded-full px-4 py-2 mb-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
                            <div className="flex -space-x-2">
                                <div className="w-6 h-6 rounded-full border-2 border-white bg-gray-300 overflow-hidden">
                                    <img src="https://randomuser.me/api/portraits/thumb/women/40.jpg" alt="User" loading="lazy" />
                                </div>
                                <div className="w-6 h-6 rounded-full border-2 border-white bg-gray-300 overflow-hidden">
                                    <img src="https://randomuser.me/api/portraits/thumb/women/41.jpg" alt="User" loading="lazy" />
                                </div>
                                <div className="w-6 h-6 rounded-full border-2 border-white bg-gray-300 overflow-hidden">
                                    <img src="https://randomuser.me/api/portraits/thumb/women/42.jpg" alt="User" loading="lazy" />
                                </div>
                            </div>
                            <span className="text-xs font-black uppercase tracking-wider">Approuvé par +5000 Vendeurs</span>
                        </div>

                        {/* 2. HEADLINE: MOTS-CLÉS RECHERCHÉS */}
                        <h1 className="text-5xl md:text-8xl font-black leading-[0.9] mb-4 md:mb-6">
                            <span className="text-xl md:text-3xl block mb-2 font-bold text-gray-500">
                                SAC VINTED • POCHETTE COLIS • EMBALLAGE PARFUMÉ
                            </span>
                            PARFUMEZ <br />
                            <span className="glitch-text" data-text="VOS VENTES">VOS VENTES</span>
                        </h1>

                        {/* TRIPTYQUE IMPACTANT - OPTION B (Large + Uppercase) */}
                        <div className="mb-8 space-y-2 font-display">
                            <p className="text-2xl md:text-3xl font-black uppercase tracking-wide leading-none">
                                <span className="text-gray-900">Vos clients </span>
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#ff0050] to-[#ff0050]">SENTENT.</span>
                            </p>
                            <p className="text-2xl md:text-3xl font-black uppercase tracking-wide leading-none text-gray-800">
                                Ils SE SOUVIENNENT.
                            </p>
                            <p className="text-2xl md:text-3xl font-black uppercase tracking-wide leading-none text-gray-800 mb-6">
                                Ils REVIENNENT.
                            </p>

                            <p className="text-lg text-gray-600 font-medium leading-relaxed max-w-2xl font-sans">
                                L'<span className="font-bold text-black">emballage colis</span> parfumé utilisé par <span className="bg-black text-white px-2 py-0.5 font-bold">+5000 vendeurs Vinted</span> pour décrocher leurs <span className="font-black text-transparent bg-clip-text bg-gradient-to-r from-[#ff0050] to-[#00f2ea]">avis 5 étoiles</span>.
                            </p>
                        </div>

                        {/* 3b. QUIZ TRIGGER - WITH CREEPYBUTTON EFFECTS */}
                        <div className="relative group mb-8">
                            {/* HIDDEN EYES (Behind, visible on hover when button moves) */}
                            <div className="absolute inset-0 bg-black rounded-2xl flex items-end justify-end pb-4 pr-6 gap-2 z-0">
                                <div className="w-3 h-3 bg-white rounded-full animate-blink flex items-center justify-center">
                                    <div className="w-1 h-1 bg-black rounded-full"></div>
                                </div>
                                <div className="w-3 h-3 bg-white rounded-full animate-blink flex items-center justify-center" style={{ animationDelay: '0.2s' }}>
                                    <div className="w-1 h-1 bg-black rounded-full"></div>
                                </div>
                            </div>

                            {/* Main button container */}
                            <motion.div
                                className="relative z-10 cursor-pointer shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] md:group-hover:shadow-[16px_16px_0px_0px_rgba(0,0,0,1)] rounded-2xl transition-all duration-300 md:group-hover:-translate-y-4 md:group-hover:-translate-x-2 active:scale-[0.97]"
                                onClick={() => setShowPromoQuiz(true)}
                            >
                                {/* Animated pulsing glow */}
                                <motion.div
                                    className="absolute -inset-[2px] bg-gradient-to-r from-[#ff0050] via-[#00f2ea] to-[#ff0050] rounded-2xl blur-sm"
                                    style={{ backgroundSize: '200% 100%' }}
                                    animate={{
                                        opacity: [0.5, 0.8, 0.5],
                                        backgroundPosition: ['0% 50%', '100% 50%', '0% 50%']
                                    }}
                                    transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                                />

                                {/* Main container - LIKE BUYBOX BUTTON */}
                                <div className="relative bg-black rounded-2xl p-4 overflow-hidden border border-white/10 group-hover:border-white/20 transition-all duration-300">
                                    {/* Liquid Metal Gradient Background */}
                                    <div className="absolute inset-0 opacity-40 group-hover:opacity-100 transition-opacity duration-700 bg-[linear-gradient(110deg,#000_30%,#333_50%,#000_70%)] bg-[length:200%_100%] animate-shimmer" />

                                    {/* Color Accent Glow at bottom */}
                                    <div className="absolute inset-x-0 bottom-0 h-[2px] opacity-50 blur-[2px] bg-gradient-to-r from-[#ff0050] to-[#00f2ea]" />

                                    {/* Content */}
                                    <div className="relative z-10 flex items-center gap-4">
                                        {/* Left: Animated Gift box */}
                                        <motion.div
                                            className="relative flex-shrink-0 w-14 h-14 rounded-xl bg-gradient-to-br from-[#ff0050] to-[#cc0040] flex items-center justify-center shadow-[0_8px_30px_rgba(255,0,80,0.5)]"
                                            animate={{
                                                y: [0, -3, 0],
                                                rotate: [-2, 2, -2]
                                            }}
                                            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                                        >
                                            {/* Rotating ring around icon */}
                                            <motion.div
                                                className="absolute inset-[-4px] rounded-xl border border-dashed border-white/30"
                                                animate={{ rotate: 360 }}
                                                transition={{ duration: 10, repeat: Infinity, ease: 'linear' }}
                                            />
                                            <Gift size={26} className="text-white" />
                                        </motion.div>

                                        {/* Center: Quiz CTA with glitch-voltage */}
                                        <div className="flex-1 min-w-0">
                                            <div className="text-white font-black text-base md:text-lg uppercase tracking-tight leading-tight">
                                                <span className="glitch-voltage" data-text="Ton cadeau t'attend">Ton cadeau t'attend</span>
                                            </div>
                                            <div className="text-white/50 text-xs md:text-sm mt-0.5 group-hover:text-white/70 transition-colors">
                                                30 sec de quiz = récompense exclusive
                                            </div>
                                        </div>

                                        {/* Right: Pulsing Arrow */}
                                        <motion.div
                                            className="flex-shrink-0 w-10 h-10 rounded-full bg-white/10 group-hover:bg-white/20 flex items-center justify-center text-white transition-colors"
                                            animate={{ x: [0, 4, 0] }}
                                            transition={{ duration: 1.5, repeat: Infinity }}
                                        >
                                            <ArrowRight size={18} />
                                        </motion.div>
                                    </div>
                                </div>
                            </motion.div>
                        </div>


                    </div >

                    {/* RIGHT: VIDEO COLUMN */}
                    < div className="relative h-[500px] md:h-[700px] w-full flex items-center justify-center mt-0 md:mt-0 z-20" >
                        <div className="relative w-full h-full max-w-md mx-auto">
                            <div className="absolute inset-0 bg-[#00f2ea] rounded-3xl transform rotate-2 blur-md opacity-30"></div>
                            <div className="absolute inset-0 bg-[#ff0050] rounded-3xl transform -rotate-2 blur-md opacity-30"></div>

                            <div className="relative z-10 rounded-3xl border-4 border-black shadow-[15px_15px_0px_0px_rgba(0,0,0,1)] overflow-hidden rotate-[-2deg] hover:rotate-0 transition-all duration-500 h-full bg-black group">
                                <video
                                    src="/assets/Videos/Sac_tiktok-parfumé_champ.mp4"
                                    autoPlay
                                    loop
                                    muted
                                    playsInline
                                    fetchpriority="high"
                                    className="w-full h-full object-cover"
                                />
                                {/* Screen Reflection Overlay */}
                                <div className="absolute inset-0 bg-gradient-to-tr from-white/20 via-transparent to-transparent opacity-50 pointer-events-none z-10 mix-blend-overlay"></div>

                                {/* Subtle Vignette */}
                                <div className="absolute inset-0 shadow-[inset_0_0_40px_rgba(0,0,0,0.4)] z-10 pointer-events-none"></div>
                            </div>
                        </div>
                    </div >

                </div >
            </section>




            <div className="bg-black text-white py-4 overflow-hidden whitespace-nowrap border-y-4 border-black rotate-1 scale-105 z-20 relative">
                <div className="inline-flex animate-marquee">
                    {[...Array(10)].map((_, i) => (
                        <span key={i} className="mx-8 text-4xl font-black uppercase italic flex items-center gap-6">
                            <span className="text-[#00f2ea]">TIKTOK VIRAL</span>
                            <span className="text-[#ff0050]">PARFUM ADDICTIF</span>
                            <span className="text-[#ffee00]">LIVRAISON 24H</span>
                        </span>
                    ))}
                </div>
            </div>



            {/* BUYBOX SNAP SECTION */}
            <div className="relative min-h-screen w-full">
                <section
                    ref={buyboxRef}
                    id="buybox"
                    data-snap-target
                    className={`w-full flex items-center justify-center bg-[#111] overflow-hidden transition-all duration-500 ${isSnapLocked
                        ? 'fixed inset-0 z-[100] h-screen'
                        : 'relative min-h-screen scroll-mt-0'
                        }`}
                >
                    <Helmet>
                        <script type="application/ld+json">
                            {JSON.stringify([
                                {
                                    "@context": "https://schema.org",
                                    "@type": "Product",
                                    "name": "Pack TikTok Viral - Unbox Creator",
                                    "description": "Pochettes d'expédition TikTok Viral. Design fluo et parfum signature pour un effet Whaou garanti sur vos vidéos. Idéal pour les créateurs.",
                                    "image": "https://unboxcreator.com/assets/logo.png",
                                    "brand": { "@type": "Brand", "name": "Unbox Creator" },
                                    "sku": "UNBOX-TK-VAR",
                                    "offers": [
                                        { "@type": "Offer", "name": "Pack 30 - S", "price": "12.99", "priceCurrency": "EUR", "availability": "https://schema.org/PreOrder", "sku": "UNBOX-TK-30-S", "areaServed": "FR", "shippingDetails": { "@type": "OfferShippingDetails", "shippingRate": { "@type": "MonetaryAmount", "value": "0", "currency": "EUR" }, "deliveryTime": { "@type": "ShippingDeliveryTime", "businessDays": { "@type": "OpeningHoursSpecification", "dayOfWeek": ["https://schema.org/Monday", "https://schema.org/Tuesday", "https://schema.org/Wednesday", "https://schema.org/Thursday", "https://schema.org/Friday"] }, "cutoffTime": "14:00:00Z", "handlingTime": { "@type": "QuantitativeValue", "minValue": 0, "maxValue": 1, "unitCode": "d" }, "transitTime": { "@type": "QuantitativeValue", "minValue": 1, "maxValue": 1, "unitCode": "d" } } } },
                                        { "@type": "Offer", "name": "Pack 30 - M", "price": "14.99", "priceCurrency": "EUR", "availability": "https://schema.org/PreOrder", "sku": "UNBOX-TK-30-M", "areaServed": "FR" },
                                        { "@type": "Offer", "name": "Pack 30 - L", "price": "16.99", "priceCurrency": "EUR", "availability": "https://schema.org/PreOrder", "sku": "UNBOX-TK-30-L", "areaServed": "FR" },
                                        { "@type": "Offer", "name": "Pack 100 - S", "price": "35.99", "priceCurrency": "EUR", "availability": "https://schema.org/PreOrder", "sku": "UNBOX-TK-100-S", "areaServed": "FR" },
                                        { "@type": "Offer", "name": "Pack 100 - M", "price": "39.99", "priceCurrency": "EUR", "availability": "https://schema.org/PreOrder", "sku": "UNBOX-TK-100-M", "areaServed": "FR" },
                                        { "@type": "Offer", "name": "Pack 100 - L", "price": "43.99", "priceCurrency": "EUR", "availability": "https://schema.org/PreOrder", "sku": "UNBOX-TK-100-L", "areaServed": "FR" },
                                        { "@type": "Offer", "name": "Pack 300 - S", "price": "89.99", "priceCurrency": "EUR", "availability": "https://schema.org/PreOrder", "sku": "UNBOX-TK-300-S", "areaServed": "FR" },
                                        { "@type": "Offer", "name": "Pack 300 - M", "price": "99.99", "priceCurrency": "EUR", "availability": "https://schema.org/PreOrder", "sku": "UNBOX-TK-300-M", "areaServed": "FR" },
                                        { "@type": "Offer", "name": "Pack 300 - L", "price": "109.99", "priceCurrency": "EUR", "availability": "https://schema.org/PreOrder", "sku": "UNBOX-TK-300-L", "areaServed": "FR" }
                                    ]
                                },
                                {
                                    "@context": "https://schema.org",
                                    "@type": "Product",
                                    "name": "Pack Vinted Pro - Unbox Creator",
                                    "description": "Pochettes Vinted Pro avec fermeture adhésive sécurisée et parfum premium. Disponible en 10 couleurs pour correspondre à votre branding.",
                                    "image": "https://unboxcreator.com/assets/logo.png",
                                    "brand": { "@type": "Brand", "name": "Unbox Creator" },
                                    "sku": "UNBOX-VT-VAR",
                                    "color": ["Noir", "Blanc", "Rose", "Bleu", "Vert", "Jaune", "Violet", "Orange", "Gris", "Rouge"],
                                    "offers": [
                                        { "@type": "Offer", "name": "Pack 30 - S", "price": "12.99", "priceCurrency": "EUR", "availability": "https://schema.org/PreOrder", "sku": "UNBOX-VT-30-S", "areaServed": "FR" },
                                        { "@type": "Offer", "name": "Pack 30 - M", "price": "14.99", "priceCurrency": "EUR", "availability": "https://schema.org/PreOrder", "sku": "UNBOX-VT-30-M", "areaServed": "FR" },
                                        { "@type": "Offer", "name": "Pack 30 - L", "price": "16.99", "priceCurrency": "EUR", "availability": "https://schema.org/PreOrder", "sku": "UNBOX-VT-30-L", "areaServed": "FR" },
                                        { "@type": "Offer", "name": "Pack 100 - S", "price": "35.99", "priceCurrency": "EUR", "availability": "https://schema.org/PreOrder", "sku": "UNBOX-VT-100-S", "areaServed": "FR" },
                                        { "@type": "Offer", "name": "Pack 100 - M", "price": "39.99", "priceCurrency": "EUR", "availability": "https://schema.org/PreOrder", "sku": "UNBOX-VT-100-M", "areaServed": "FR" },
                                        { "@type": "Offer", "name": "Pack 100 - L", "price": "43.99", "priceCurrency": "EUR", "availability": "https://schema.org/PreOrder", "sku": "UNBOX-VT-100-L", "areaServed": "FR" },
                                        { "@type": "Offer", "name": "Pack 300 - S", "price": "89.99", "priceCurrency": "EUR", "availability": "https://schema.org/PreOrder", "sku": "UNBOX-VT-300-S", "areaServed": "FR" },
                                        { "@type": "Offer", "name": "Pack 300 - M", "price": "99.99", "priceCurrency": "EUR", "availability": "https://schema.org/PreOrder", "sku": "UNBOX-VT-300-M", "areaServed": "FR" },
                                        { "@type": "Offer", "name": "Pack 300 - L", "price": "109.99", "priceCurrency": "EUR", "availability": "https://schema.org/PreOrder", "sku": "UNBOX-VT-300-L", "areaServed": "FR" }
                                    ]
                                },
                                {
                                    "@context": "https://schema.org",
                                    "@type": "Product",
                                    "name": "Pack Unicolor - Unbox Creator",
                                    "description": "Sacs d'expédition monochromes haute qualité. Simple, élégant et économique pour les gros volumes.",
                                    "image": "https://unboxcreator.com/assets/logo.png",
                                    "brand": { "@type": "Brand", "name": "Unbox Creator" },
                                    "sku": "UNBOX-UC-VAR",
                                    "offers": [
                                        { "@type": "Offer", "name": "Pack 30 - S", "price": "9.99", "priceCurrency": "EUR", "availability": "https://schema.org/PreOrder", "sku": "UNBOX-UC-30-S", "areaServed": "FR" },
                                        { "@type": "Offer", "name": "Pack 30 - M", "price": "11.99", "priceCurrency": "EUR", "availability": "https://schema.org/PreOrder", "sku": "UNBOX-UC-30-M", "areaServed": "FR" },
                                        { "@type": "Offer", "name": "Pack 30 - L", "price": "14.99", "priceCurrency": "EUR", "availability": "https://schema.org/PreOrder", "sku": "UNBOX-UC-30-L", "areaServed": "FR" },
                                        { "@type": "Offer", "name": "Pack 100 - S", "price": "24.99", "priceCurrency": "EUR", "availability": "https://schema.org/PreOrder", "sku": "UNBOX-UC-100-S", "areaServed": "FR" },
                                        { "@type": "Offer", "name": "Pack 100 - M", "price": "29.99", "priceCurrency": "EUR", "availability": "https://schema.org/PreOrder", "sku": "UNBOX-UC-100-M", "areaServed": "FR" },
                                        { "@type": "Offer", "name": "Pack 100 - L", "price": "39.99", "priceCurrency": "EUR", "availability": "https://schema.org/PreOrder", "sku": "UNBOX-UC-100-L", "areaServed": "FR" },
                                        { "@type": "Offer", "name": "Pack 300 - S", "price": "69.99", "priceCurrency": "EUR", "availability": "https://schema.org/PreOrder", "sku": "UNBOX-UC-300-S", "areaServed": "FR" },
                                        { "@type": "Offer", "name": "Pack 300 - M", "price": "79.99", "priceCurrency": "EUR", "availability": "https://schema.org/PreOrder", "sku": "UNBOX-UC-300-M", "areaServed": "FR" },
                                        { "@type": "Offer", "name": "Pack 300 - L", "price": "99.99", "priceCurrency": "EUR", "availability": "https://schema.org/PreOrder", "sku": "UNBOX-UC-300-L", "areaServed": "FR" }
                                    ]
                                }
                            ])}
                        </script>
                    </Helmet>
                    <Suspense fallback={<div className="text-white">Loading Configurator...</div>}>
                        <ConfiguratorHero promoConfig={promoConfig} />
                    </Suspense>
                </section>
            </div>
            {/* SECTION : VINTED BUSINESS (MOVED HERE FROM BELOW) */}
            <section className="py-24 px-6 bg-black text-white overflow-hidden">
                <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center">
                    <div className="order-1">
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#09B1BA]/10 text-[#09B1BA] border border-[#09B1BA] mb-6 shadow-[0_0_15px_rgba(9,177,186,0.3)]">
                            <Star size={16} fill="currentColor" className="text-[#09B1BA]" />
                            <span className="text-sm font-black uppercase tracking-wider">VINTED PRO</span>
                        </div>
                        <h2 className="text-5xl md:text-7xl font-black font-display mb-8 leading-none text-white">
                            OBJECTIF <br />
                            <span className="glitch-voltage text-transparent bg-clip-text bg-gradient-to-r from-[#00f2ea] to-white" data-text="5 ÉTOILES">5 ÉTOILES</span>
                        </h2>
                        <p className="text-xl text-gray-400 mb-8 font-medium leading-relaxed">
                            Sur Vinted, la photo vend, mais <span className="text-white font-bold">l'emballage fidélise.</span><br />
                            Offrez une expérience "Unboxing" digne des plus grandes marques, directement depuis votre chambre.
                        </p>
                        <div className="flex flex-col gap-4">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-full bg-[#222] flex items-center justify-center text-[#00f2ea]">
                                    <Shield size={24} />
                                </div>
                                <div>
                                    <div className="font-black text-xl text-white">Zéro Litige</div>
                                    <div className="text-sm text-gray-500">Protection indéchirable 60 microns.</div>
                                </div>
                            </div>
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-full bg-[#222] flex items-center justify-center text-[#ffee00]">
                                    <Star size={24} />
                                </div>
                                <div>
                                    <div className="font-black text-xl text-white">Avis Positifs</div>
                                    <div className="text-sm text-gray-500">Vos acheteurs posteront leur réception.</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="order-2 relative group">
                        <div className="absolute inset-0 bg-[#00f2ea] rounded-3xl transform rotate-2 group-hover:rotate-1 transition-all duration-500 opacity-20"></div>
                        <div className="relative z-10 rounded-3xl border-4 border-white shadow-[12px_12px_0px_0px_rgba(255,255,255,0.2)] rotate-[-2deg] group-hover:rotate-0 transition-all duration-500 overflow-hidden aspect-[4/5]">
                            <img
                                src="/assets/pochette-vinted-emballage-colis-pack-parfume.webp"
                                alt="Sac vinted parfumé - Emballage colis vinted - Pochette colis premium"
                                loading="lazy"
                                className="w-full h-full object-cover scale-110"
                            />
                        </div>

                        {/* Badge Flottant */}
                        <a href="/#buybox" className="absolute -bottom-6 -left-6 z-20 bg-black text-white border-4 border-white p-4 rounded-xl shadow-[8px_8px_0px_0px_rgba(255,255,255,0.2)] rotate-3 hover:rotate-0 transition-all hover:scale-105">
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse"></div>
                                <span className="font-black text-sm">ACHETER MAINTENANT</span>
                            </div>
                        </a>
                    </div>
                </div>
            </section>

            {/* Scent Fusion Section - Moved here for CRO (After Offer) */}
            <Suspense fallback={<div className="text-white">Loading...</div>}>
                <ScentFusionSection />
            </Suspense>

            {/* SECTION : RECOMMANDÉ PAR LES MEILLEURS VENDEURS (Fusion Carousel + Testimonials) */}
            <section className="py-24 bg-white overflow-hidden">
                {/* Text Container - Full Width like AG1 */}
                <div className="px-6 md:px-12 lg:px-24">
                    {/* TITRE DE LA SECTION 5 */}
                    <h2 className="text-3xl md:text-6xl font-black font-display text-left mb-6 leading-[0.95] animate-breathing">
                        RECOMMANDÉ PAR LES <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#09B1BA] to-gray-900">MEILLEURS VENDEURS</span>
                    </h2>
                    <p className="text-lg md:text-xl text-gray-600 text-left mb-16 leading-relaxed">
                        Des top vendeurs TikTok Shop aux e-commerçants professionnels – Unbox Creator est le secret de ceux qui veulent se démarquer.
                    </p>

                    {/* STRUCTURE CAROUSEL DE LA SECTION 4 AVEC IMAGES TESTIMONIALS */}
                    <div className="relative -mr-6 md:-mr-12 lg:-mr-24">
                        {/* Carousel Container - starts aligned with title, overflows right */}
                        <div
                            ref={carouselRef}
                            id="ag1-carousel"
                            className="flex gap-5 overflow-x-auto pb-8 pr-6 scroll-smooth"
                            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                        >
                            {/* Testimonial Cards in Carousel Format */}
                            {testimonials.map((t, idx) => (
                                <div key={idx} className="relative rounded-2xl overflow-hidden h-[420px] w-[300px] md:h-[520px] md:w-[380px] lg:h-[580px] lg:w-[420px] shrink-0 snap-start group">
                                    <img
                                        src={t.image}
                                        alt={t.name}
                                        loading="lazy"
                                        className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 ${t.className || ''}`}
                                    />
                                    <div className="absolute bottom-0 left-0 right-0 h-[180px] backdrop-blur-xl bg-gradient-to-t from-black/80 via-black/60 to-transparent" />
                                    <div className="absolute bottom-0 left-0 right-0 p-6">
                                        <p className="text-xs text-[#09B1BA] font-bold uppercase tracking-wider mb-2">{t.title}</p>
                                        <h3 className="text-xl font-black text-white mb-3">{t.name}</h3>
                                        <p className="text-sm text-white/90 leading-relaxed">
                                            {t.quote}
                                        </p>
                                    </div>
                                </div>
                            ))}

                            {/* Additional Lifestyle Cards */}
                            <div className="relative rounded-2xl overflow-hidden h-[420px] w-[300px] md:h-[520px] md:w-[380px] lg:h-[580px] lg:w-[420px] shrink-0 snap-start group">
                                <img src="/assets/experience-olfactive-sac-vinted-parfume-cliente.webp" alt="Sac expédition parfumé - Pochette envoi colis vinted parfumée" loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                                <div className="absolute bottom-0 left-0 right-0 h-[140px] backdrop-blur-xl bg-gradient-to-t from-black/70 via-black/50 to-transparent" />
                                <div className="absolute bottom-0 left-0 right-0 p-6">
                                    <h3 className="text-lg font-semibold text-white mb-2">Fidélisation olfactive</h3>
                                    <p className="text-sm text-white/90 leading-relaxed">
                                        Notre technologie de parfum signature crée une connexion émotionnelle instantanée avec vos clients.
                                    </p>
                                </div>
                            </div>

                            <div className="relative rounded-2xl overflow-hidden h-[420px] w-[300px] md:h-[520px] md:w-[380px] lg:h-[580px] lg:w-[420px] shrink-0 snap-start group">
                                <img src="/assets/unboxing-viral-tiktok-shop-emballage-5-etoiles.webp" alt="Emballage colis tiktok shop - Sac expédition viral avis 5 étoiles" loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                                <div className="absolute bottom-0 left-0 right-0 h-[140px] backdrop-blur-xl bg-gradient-to-t from-black/70 via-black/50 to-transparent" />
                                <div className="absolute bottom-0 left-0 right-0 p-6">
                                    <h3 className="text-lg font-semibold text-white mb-2">Avis 5 étoiles</h3>
                                    <p className="text-sm text-white/90 leading-relaxed">
                                        L'effet Whaou qui transforme chaque client en ambassadeur de votre marque.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Bottom Centered Arrows - AG1 Style */}
                        <div className="flex justify-center items-center gap-6 mt-4">
                            <button
                                onClick={() => document.getElementById('ag1-carousel').scrollBy({ left: -400, behavior: 'smooth' })}
                                className="w-10 h-10 flex items-center justify-center text-gray-600 hover:text-gray-900 transition-colors"
                            >
                                <ChevronLeft size={28} strokeWidth={1.5} />
                            </button>
                            <button
                                onClick={() => document.getElementById('ag1-carousel').scrollBy({ left: 400, behavior: 'smooth' })}
                                className="w-10 h-10 flex items-center justify-center text-gray-600 hover:text-gray-900 transition-colors"
                            >
                                <ChevronRight size={28} strokeWidth={1.5} />
                            </button>
                        </div>
                    </div>
                </div>
            </section>





            {/* MARKETING: GROWTH TIMELINE */}
            < GrowthTimeline />


            {/* MARKETING: COMPARISON TABLE */}
            {/* MARKETING: COMPARISON TABLE */}
            <Suspense fallback={<div className="h-96 bg-gray-50 flex items-center justify-center"><Loader2 className="animate-spin" /></div>}>
                <ComparisonTable />
            </Suspense>

            {/* MARKETING: SOCIAL PROOF REVIEWS - REMOVED */}

            <Suspense fallback={<div className="h-96 bg-black flex items-center justify-center"><Loader2 className="animate-spin text-white" /></div>}>
                <SolutionSection />
            </Suspense>




            {/* END CONFIGURATOR_SECTION_HIDDEN */}

            {/* SECTION SEO : COULEURS, DIMENSIONS, VENTE EN GROS */}
            {/* <SECTION SEO AMELIOREE> */}
            <section className="py-24 bg-black relative overflow-hidden border-t border-white/10">
                <div className="absolute inset-0 bg-[radial-gradient(#333_1px,transparent_1px)] [background-size:16px_16px] opacity-20 pointer-events-none"></div>

                <div className="max-w-7xl mx-auto px-6 relative z-10">

                    {/* SEO H2 : 10 Couleurs */}
                    <div className="mb-24">
                        <h2 className="text-4xl md:text-5xl font-black font-display text-center mb-6 text-white uppercase tracking-tight">
                            SACS PARFUMÉS EN <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#ff0050] to-[#00f2ea]">10 COULEURS</span>
                        </h2>
                        <p className="text-lg md:text-xl text-center text-gray-400 max-w-4xl mx-auto leading-relaxed font-sans">
                            <span className="text-white font-bold">Noir, Gris, Rouge, Rose, Blanc, Bleu, Jaune, Orange, Vert, Bordeaux</span>.
                            Toutes nos couleurs bénéficient d'un <span className="text-[#00f2ea] font-black">effet brillant métallisé premium</span>, sont <span className="text-[#ff0050] font-black">parfumées</span> et ultra-résistantes (60 microns).
                        </p>
                    </div>

                    {/* SEO H2 : 3 Dimensions */}
                    <div className="mb-24 grid md:grid-cols-2 gap-12 items-center">
                        <div className="order-2 md:order-1">
                            <h2 className="text-4xl md:text-5xl font-black font-display mb-6 text-white uppercase tracking-tight">
                                3 TAILLES POUR <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#ff0050] to-[#00f2ea]">TOUS VOS ENVOIS</span>
                            </h2>
                            <ul className="space-y-4 text-gray-400 font-sans text-lg">
                                <li className="flex items-center gap-3">
                                    <span className="w-2 h-2 bg-[#00f2ea] rounded-full"></span>
                                    <span><strong>S (20x30cm)</strong> : Accessoires, Bijoux, T-shirts</span>
                                </li>
                                <li className="flex items-center gap-3">
                                    <span className="w-2 h-2 bg-[#ff0050] rounded-full"></span>
                                    <span><strong>M (30x40cm)</strong> : Jeans, Pulls, Robes (Standard Vinted)</span>
                                </li>
                                <li className="flex items-center gap-3">
                                    <span className="w-2 h-2 bg-[#ffee00] rounded-full"></span>
                                    <span><strong>L (40x50cm)</strong> : Manteaux, Bottes, Gros Colis</span>
                                </li>
                            </ul>
                        </div>
                        <div className="order-1 md:order-2 bg-white/5 border border-white/10 rounded-3xl p-8 backdrop-blur-sm">
                            <div className="grid grid-cols-3 gap-6 h-64 items-end">
                                <SizeBar height={60} color="cyan" label="S" sizeCm={30} delay={0} />
                                <SizeBar height={75} color="magenta" label="M" sizeCm={40} delay={0.2} />
                                <SizeBar height={90} color="yellow" label="L" sizeCm={50} delay={0.4} />
                            </div>
                        </div>
                    </div>

                    {/* SEO H2 : Vente en Gros */}
                    <div className="mb-24 bg-[#111] border border-white/10 rounded-3xl p-8 md:p-16 text-center relative overflow-hidden group">
                        <div className="absolute inset-0 bg-gradient-to-r from-[#ff0050]/10 to-[#00f2ea]/10 opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>

                        <h2 className="text-3xl md:text-5xl font-black font-display mb-6 text-white uppercase tracking-tight relative z-10">
                            VENTE EN GROS & <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#ff0050] to-[#00f2ea]">B2B</span>
                        </h2>
                        <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-8 relative z-10">
                            <strong>À partir de 5000 unités</strong> : Tarifs dégressifs, personnalisation logo, production sur-mesure.
                            La solution logistique n°1 pour les Power-Sellers.
                        </p>
                        <div className="relative z-10">
                            <a
                                href="/custom"
                                className="inline-flex items-center gap-2 bg-white text-black px-8 py-4 rounded-full font-black uppercase tracking-wider hover:bg-[#00f2ea] hover:scale-105 transition-all duration-300"
                            >
                                Demander un Devis Pro <ArrowRight size={20} />
                            </a>
                        </div>
                    </div>


                </div>
            </section>
            {/* </SECTION SEO AMELIOREE> */}

            <section className="py-24 px-6 bg-white">
                <div className="max-w-7xl mx-auto">
                    <h2 className="text-4xl md:text-7xl font-black font-display text-center mb-16 leading-none"><span className="glitch-text" data-text="ILS EXPLOSENT">ILS EXPLOSENT</span></h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 w-full">
                        {[
                            { user: "@vinted_pro", views: "1.5M", video: "/assets/Videos/unboxing-vinted-fidelisation-client-satisfaction.mp4", image: "/assets/Thumbnails/video-tiktok-unboxing-viral-miniature.webp" },
                            { user: "@ugc_creator", views: "2.8M", video: "/assets/Videos/creation_video_ugc.mp4", image: "/assets/Thumbnails/ugc_creator.webp" },
                            { user: "@business_booster", views: "3.2M", video: "/assets/Videos/temoignage-vendeuse-ventes-triplees-emballage.mp4", image: "/assets/Thumbnails/video-vendeur-vinted-professionnel.webp" },
                            { user: "@tiktok_star", views: "4.5M", video: "/assets/Videos/unboxing-viral-tiktok-shop-sac-parfume.mp4", image: "/assets/Thumbnails/video-croissance-ventes-business.webp" }
                        ].map((item, i) => (
                            <VideoCard key={i} {...item} />
                        ))}
                    </div>
                </div>
            </section>

            {/* MARKETING: LOOX STYLE REVIEWS (LOCAL ONLY) */}
            <Suspense fallback={<div className="h-96 bg-white flex items-center justify-center"><Loader2 className="animate-spin" /></div>}>
                <LooxReviews /> {/* Force Rebuild Trigger: Story Carousel Update */}
            </Suspense>

            {/* GUIDE / FAQ VISUELLE */}
            {/* GUIDE / FAQ VISUELLE */}
            <Suspense fallback={<div className="h-96 bg-white" />}>
                <GuideSection />
            </Suspense>

            {/* MARKETING: RISK REVERSAL GUARANTEE */}
            {/* MARKETING: RISK REVERSAL GUARANTEE */}
            <Suspense fallback={<div className="h-48 bg-gray-50" />}>
                <RiskReversalGuarantee />
            </Suspense>

            {/* SECTION POUR LES NOUVEAUX VENDEURS */}
            <Suspense fallback={<div className="h-96 bg-[#0a0a0a]" />}>
                <NewBrandSection />
            </Suspense>

            <section className="py-32 bg-black text-white text-center px-6">
                <div className="max-w-4xl mx-auto">
                    <h2 className="text-6xl md:text-9xl mb-8 leading-none font-black font-display">PRÊT À<br /><span className="glitch-voltage" data-text="EXPLOSER ?">EXPLOSER ?</span></h2>
                    <p className="text-2xl text-gray-400 mb-12">Rejoignez les 2500+ marques qui explosent.</p>

                    {/* BOUTON FOOTER CREEPY */}
                    <a href="/#buybox" className="inline-block">
                        <CreepyButton
                            text="ACHETEZ MAINTENANT"
                            icon={ArrowRight}
                            className="px-8 md:px-16 py-4 md:py-8 text-xl md:text-2xl"
                            borderColor="border-white"
                        />
                    </a>
                </div>
            </section>






            {/* PRIME DRAWER */}
            <PrimeDrawer
                isOpen={showPrimeDrawer}
                onClose={() => setShowPrimeDrawer(false)}
            />

            {/* DELIVERY INFO MODAL */}
            <DeliveryInfoModal
                isOpen={showDeliveryInfo}
                onClose={() => setShowDeliveryInfo(false)}
            />

            {/* PROMO QUIZ MODAL */}
            <PromoQuiz
                isOpen={showPromoQuiz}
                onClose={() => setShowPromoQuiz(false)}
                onComplete={(config) => {
                    setPromoConfig(config);
                    // Scroll to buybox after a short delay
                    setTimeout(() => {
                        document.getElementById('buybox')?.scrollIntoView({ behavior: 'smooth' });
                    }, 300);
                }}
            />
        </div >
    );
};

export default HomePage;

